import {
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Close, Plus, Search2 } from '@/client/components/icons/ContinuIcons';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import AIAgentService from './api/AIAgentService';
import SolidButton from '../../shared/buttons/SolidButton';
import { UserConversationEdge } from '@/client/services/api/graphql/gql/graphql';
import { format } from 'date-fns';
import { useAIAgentStore } from './AIAgentStore';
import { useDebounceValue } from 'usehooks-ts';
import { useListUserConversations } from './hooks/useListUserConversations';
import { useTranslation } from 'react-i18next';

interface ConversationHistoryItemProps {
  conversation: UserConversationEdge;
}

function ConversationHistoryItem({ conversation }: ConversationHistoryItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { setCurrentConversationId, setActiveTab } = useAIAgentStore();

  const { node } = conversation;

  const { query, createdAt, conversation: conversationId } = node;

  const queryClient = useQueryClient();

  const { mutateAsync: archiveConversation, status } = useMutation({
    mutationFn: () => AIAgentService.archiveUserAIConversation(conversationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-agent-conversations'] }),
  });

  return (
    <Box
      paddingX={4}
      paddingY={6}
      backgroundColor="white"
      border="1px solid"
      borderColor="neutral.200"
      borderRadius="8px"
      cursor="pointer"
      position="relative"
      _hover={{ backgroundColor: 'neutral.50' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        setCurrentConversationId(conversationId);
        setActiveTab('conversation');
      }}
    >
      <IconButton
        isLoading={status === 'loading'}
        aria-label="Close conversation"
        icon={<Close color="neutral.800" />}
        size="sm"
        variant="ghost"
        position="absolute"
        top={2}
        right={2}
        opacity={isHovered ? 1 : 0}
        _hover={{ backgroundColor: 'neutral.100' }}
        transition="opacity 400ms"
        onClick={(e) => {
          e.stopPropagation();
          archiveConversation();
        }}
      />

      <Stack spacing={2}>
        <Text fontSize="12px" fontWeight={500} color="baseBlack">
          "{query}"
        </Text>

        <Text fontSize="12px" fontWeight={500} color="neutral.700">
          {format(new Date(createdAt), 'MMMM d, yyyy')}
        </Text>
      </Stack>
    </Box>
  );
}

export default function AIAgentConversationHistory() {
  const { t } = useTranslation();
  const { setActiveTab, setCurrentConversationId } = useAIAgentStore();
  const [searchTerm, setSearchTerm] = useDebounceValue('', 300);
  const queryClient = useQueryClient();

  const { data: conversations } = useListUserConversations(searchTerm);

  useEffect(() => {
    if (conversations?.edges?.length === 0 && !searchTerm) {
      setActiveTab('conversation');
    }
  }, [conversations]);

  return (
    <Flex flexDirection="column" justifyContent="space-between" maxHeight="100%">
      <Stack flex={1} overflow="hidden">
        <InputGroup>
          <InputLeftElement>
            <Search2 color="neutral.800" />
          </InputLeftElement>

          <Input
            variant="neutral"
            placeholder={`${t('aiAgent.history.search.placeholder')}`}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <Stack spacing={3} overflowY="auto" marginBottom={3}>
          {conversations?.edges?.map((conversation) => (
            <ConversationHistoryItem key={conversation.node.id} conversation={conversation} />
          ))}
        </Stack>
      </Stack>

      <Box height={100}>
        <SolidButton
          variant="AIAgent"
          width="full"
          leftIcon={<Plus />}
          onClick={() => {
            queryClient.removeQueries({ queryKey: ['conversation'] });
            setCurrentConversationId('');
            setActiveTab('conversation');
          }}
        >
          {t('aiAgent.newConversation')}
        </SolidButton>
      </Box>
    </Flex>
  );
}
